/* eslint-disable sonarjs/cognitive-complexity */
import { Stack } from 'component-library';
import { SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { BenchmarkType, PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { Dot } from '@/components/Charts/components/Dot';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useMatchCurrentPage } from '@/hooks/useMatchCurrentPage';
import { usePathSet } from '@/hooks/usePathSet';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { colorPalette } from '@/theme/colorPalette';
import { isNullish } from '@/utils/booleans';
import { formatUnit, unitToTonne } from '@/utils/formatting';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { Bar, BarChart, Chart, TooltipProps } from './components/Chart';
import { RenderBenchmarkReferenceLine } from './components/ReferenceLine';
import { ChartTooltip, ChartTooltipTextPrimary } from './components/Tooltip';
import { useAggregationBenchmarks, useBenchmarksForAggregation } from './hooks/useAggregationBenchmarks';
import { NoChartData } from './NoChartData';
import { ChartProps } from './types';
import { getBenchmarkLabel, kgToTonneFormatter, minMaxAxisDomain } from './utils';
import { getAggregationGraphColor } from './utils/getAggregationGraphColor';

export type ScalarDataPoint = {
  id: string;
  value: number;
  name: string;
  unit: UnitEnum;
};

export type ItemizedBarChartProps = {
  data: ScalarDataPoint[];
  indicator: GraphTileType;
  unit: UnitEnum;
  benchmarks?: ReturnType<typeof useAggregationBenchmarks> | null;
  aggregationBenchmarks?: number[] | null;
  isPlotCountLimited: boolean;
  yAxisLabel?: string;
  customBenchmark?: CustomBenchmarkSuccessResponse | null;
} & Omit<ChartProps, 'data' | 'benchmarks'>;

const RECTANGLE_RADIUS = 2 as const;

const FORMATTERS = {
  [GraphTileType.historic_below_ground_carbon]: kgToTonneFormatter,
  [GraphTileType.historic_above_ground_carbon]: kgToTonneFormatter,
};

export const ItemizedBarChart = ({
  data,
  indicator,
  unit,
  benchmarks,
  aggregationBenchmarks,
  isPlotCountLimited,
  yAxisLabel,
  customBenchmark,
  ...delegated
}: ItemizedBarChartProps) => {
  const { t } = useTranslation();
  const pathSet = usePathSet();

  const isPageWithPlotsTable = useMatchCurrentPage([
    pathSet.carbonDetails,
    pathSet.waterDetails,
    pathSet.biodiversityDetails,
    pathSet.soilDetails,
    pathSet.socialDetails,
    pathSet.plots,
  ]);

  const fillColor = getAggregationGraphColor(indicator) ?? '#48A14D';

  let chartData = data;

  if (data.length === 0) {
    Logger.error(`Not enough data to render chart (indicator: ${indicator})`);
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const formatter = FORMATTERS[indicator as keyof typeof FORMATTERS];
  if (formatter) {
    chartData = data.flatMap((dataPoint) => {
      const value = formatter(dataPoint as unknown as PlotReportFactElement<number>);
      if (isNullish(value)) {
        return [];
      }
      return {
        ...dataPoint,
        value,
      };
    });
  }

  const xAxisProps = {
    dataKey: 'name',
    type: 'category' as const,
    height: 40,
    tickFormatter: (value: string) => (value.length > 6 ? `${value.slice(0, 6)}` : value),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tick: (tickProps: any) => {
      const { x, y, payload } = tickProps;
      const value = payload.value as string;
      const displayValue = value.length > 6 ? `${value.slice(0, 6)}...` : value;
      return (
        <text x={x} y={y} dx={-16} style={{ fontSize: '12px', fill: 'rgba(0, 0, 0, 0.60)', maxWidth: '40px' }}>
          {displayValue}
        </text>
      );
    },
  };

  const chartConfig = {
    value: {
      color: fillColor.dark,
    },
  };

  const benchmarkHighFact = Array.isArray(benchmarks?.high?.value)
    ? benchmarks?.high?.value?.at(0)
    : (benchmarks?.high as PlotReportFactElement<number | null>);
  const benchmarkModerateFact = Array.isArray(benchmarks?.moderate?.value)
    ? benchmarks?.moderate?.value?.at(0)
    : (benchmarks?.moderate as PlotReportFactElement<number | null>);
  const benchmarkLowFact = Array.isArray(benchmarks?.low?.value)
    ? benchmarks?.low?.value?.at(0)
    : (benchmarks?.low as PlotReportFactElement<number | null>);
  const benchmarkVeryLowFact = Array.isArray(benchmarks?.very_low?.value)
    ? benchmarks?.very_low?.value?.at(0)
    : (benchmarks?.very_low as PlotReportFactElement<number | null>);
  const benchmarkUnchangedFact = Array.isArray(benchmarks?.unchanged?.value)
    ? benchmarks?.unchanged?.value?.at(0)
    : (benchmarks?.unchanged as PlotReportFactElement<number | null>);

  const benchmarkValues = customBenchmark
    ? {
        high: customBenchmark.high_value ?? undefined,
        moderate: customBenchmark.moderate_value ?? undefined,
        low: customBenchmark.low_value ?? undefined,
      }
    : {
        high: formatter ? formatter(benchmarkHighFact) : benchmarkHighFact?.value,
        moderate: formatter ? formatter(benchmarkModerateFact) : benchmarkModerateFact?.value,
        low: formatter ? formatter(benchmarkLowFact) : benchmarkLowFact?.value,
        very_low: formatter ? formatter(benchmarkVeryLowFact) : benchmarkVeryLowFact?.value,
        unchanged: formatter ? formatter(benchmarkUnchangedFact) : benchmarkUnchangedFact?.value,
      };

  const formattedUnit = (indicator as keyof typeof FORMATTERS) ? formatUnit(unitToTonne(unit)) : formatUnit(unit);

  return (
    <>
      <Chart.Container config={chartConfig} data={chartData} data-testid='aggregation-value-graph' {...delegated}>
        <BarChart data={chartData}>
          <defs>
            <linearGradient id='barFill' x1='0' y1='0' x2='0' y2='1'>
              <stop offset='0%' stopColor={fillColor.dark} stopOpacity={1} />
              <stop offset='100%' stopColor={fillColor.light} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Bar
            fill='url(#barFill)'
            dataKey='value'
            radius={[RECTANGLE_RADIUS, RECTANGLE_RADIUS, 0, 0]}
            activeBar={(props: unknown) => <CustomActiveBar {...(props as ActiveBarProps)} color={fillColor.dark} />}
            className='aggregated-bar-graph-bar'
          />
          {benchmarkValues.high &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.high,
              benchmark: BenchmarkType.high,
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.high'), customBenchmark?.name),
            })}
          {benchmarkValues.moderate &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.moderate,
              benchmark: BenchmarkType.moderate,
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.moderate'), customBenchmark?.name),
            })}
          {benchmarkValues.low &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.low,
              benchmark: BenchmarkType.low,
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.low'), customBenchmark?.name),
            })}
          {benchmarkValues?.very_low &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.very_low,
              benchmark: BenchmarkType.very_low,
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.very_low'), customBenchmark?.name),
            })}
          {benchmarkValues?.unchanged &&
            RenderBenchmarkReferenceLine({
              value: benchmarkValues.unchanged,
              benchmark: BenchmarkType.unchanged,
              labelOverride: getBenchmarkLabel(true, t('global.benchmark.unchanged'), customBenchmark?.name),
            })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 10 }} className='aggregated-bar-graph-x-axis' />
          <Chart.YAxis domain={minMaxAxisDomain(0.8, 1.2)} className='aggregated-bar-graph-y-axis'>
            <Chart.Label>{formattedUnit || `${yAxisLabel}`}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip
            content={<CustomTooltip unit={formattedUnit} benchmarks={aggregationBenchmarks} />}
            cursor={false}
          />
        </BarChart>
      </Chart.Container>
      {isPlotCountLimited && (
        <span className='typography-caption text-text-secondary m-2 ml-7'>
          <i className='ri-information-line mr-2' />
          {t(isPageWithPlotsTable ? 'graph.chartStyle.plotLimitWarningPlotTable' : 'graph.chartStyle.plotLimitWarning')}
        </span>
      )}
    </>
  );
};

export const CustomTooltip = ({
  active,
  payload,
  unit,
  benchmarks,
}: TooltipProps<ValueType, NameType> & {
  unit: string | undefined;
  benchmarks?: ReturnType<typeof useBenchmarksForAggregation> | null;
}) => {
  const { t } = useTranslation();
  const value = payload?.find((data) => data.dataKey === 'value');

  if (!active) return null;

  const factValue = !isNullish(value?.payload?.value)
    ? `${getDisplayNumber(value?.payload?.value as string, window.navigator.language)} ${unit}`
    : t('global.analysis.noData');

  return (
    <ChartTooltip>
      {benchmarks && benchmarks.length > 0 && (
        <TooltipBenchmarkPill value={value?.payload?.value} benchmarks={benchmarks} />
      )}
      <Stack spacing={8} direction='row' className='w-full justify-between'>
        {value && <ChartTooltipTextPrimary>{value.payload.name}</ChartTooltipTextPrimary>}
        <ChartTooltipTextPrimary>{factValue}</ChartTooltipTextPrimary>
      </Stack>
    </ChartTooltip>
  );
};

const TooltipBenchmarkPill = ({ value, benchmarks }: { value: number; benchmarks: number[] }) => {
  const benchmarkType = useBenchmarkForFact(benchmarks, value);

  if (!benchmarkType) return null;

  return (
    <div className='typography-body2 text-text-secondary'>
      <BenchmarkPill benchmarkType={benchmarkType} />
    </div>
  );
};

type ActiveBarProps = SVGProps<SVGElement> & { background: { y: number }; color: string };

const CustomActiveBar = ({ x, y, width, height, background, color }: ActiveBarProps) => {
  if (!x || !y || !width || !height) return <></>;

  const barMiddleX = (x as number) + (width as number) / 2;
  const barBottom = (y as number) + (height as number);
  const barTop = background.y;

  const dotProps = {
    cx: barMiddleX,
    cy: y as number,
    fill: '#000000',
    strokeWidth: 0,
    opacity: 1,
  };

  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={color} ry={RECTANGLE_RADIUS} />
      <line x1={barMiddleX} x2={barMiddleX} y1={barTop} y2={barBottom} stroke={colorPalette.divider} strokeWidth={1} />
      <Dot.Container {...dotProps}>
        <Dot.Active {...dotProps} fill='#000000' />
      </Dot.Container>
    </g>
  );
};
